import { useEffect, useState } from "react";
import { Header } from "@/components/layout/Header";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  CheckCircle2,
  TrendingUp,
  Users,
  MessageSquare,
  Brain,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Loader2,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Progress } from "@/components/ui/progress";
import footerFunding from "@/assets/footer-funding.jpeg";
import { sendBusinessFormEmail } from "@/services/api/endpoints/teacher.api";
import { useAppDispatch } from "@/store/hooks";
import { showToast } from "@/store/slices/uiSlice";
import { logger } from "@/utils/logger";
import { SEOPresets } from "@/components/SEO";

export default function ForBusinesses() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [quizAnswers, setQuizAnswers] = useState({
    teamSize: "",
    englishCommunication: "",
    challenges: "",
  });
  const [email, setEmail] = useState("");
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const totalSteps = 4; // 3 questions + email

  // Mock data for dashboard metrics
  const dailyUsageData = [
    {
      day: "Mon",
      sessions: 24,
    },
    {
      day: "Tue",
      sessions: 31,
    },
    {
      day: "Wed",
      sessions: 28,
    },
    {
      day: "Thu",
      sessions: 35,
    },
    {
      day: "Fri",
      sessions: 29,
    },
  ];
  const progressData = [
    {
      week: "M1",
      score: 62,
    },
    {
      week: "M2",
      score: 68,
    },
    {
      week: "M3",
      score: 65,
    },
    {
      week: "M4",
      score: 74,
    },
    {
      week: "M5",
      score: 71,
    },
    {
      week: "M6",
      score: 79,
    },
    {
      week: "M7",
      score: 82,
    },
    {
      week: "M8",
      score: 88,
    },
  ];
  const conversationTypesData = [
    {
      type: "Daily Stand-ups",
      count: 45,
    },
    {
      type: "Sprint Planning",
      count: 32,
    },
    {
      type: "Code Reviews",
      count: 28,
    },
    {
      type: "Performance Review",
      count: 18,
    },
    {
      type: "Client Meetings",
      count: 22,
    },
  ];
  const engagementData = [
    {
      name: "Active",
      value: 75,
    },
    {
      name: "Inactive",
      value: 25,
    },
  ];
  const COLORS = ["hsl(var(--primary))", "hsl(var(--muted))"];
  const formatAnswersForEmail = () => {
    return [
      `Team size: ${quizAnswers.teamSize}`,
      `English communication: ${quizAnswers.englishCommunication}`,
      `Communication challenges: ${quizAnswers.challenges}`,
    ];
  };
  const handleFormSubmission = async () => {
    if (!email) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const answers = formatAnswersForEmail();
      await sendBusinessFormEmail({
        email,
        answers,
        title: "[GlobalCoders] New Form Submission",
      });
      setQuizSubmitted(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    } catch (error: any) {
      logger.error("Error submitting business form:", error);
      setSubmitError(
        error.message || "Failed to submit form. Please try again."
      );
      dispatch(
        showToast({
          message: "Failed to submit form. Please try again.",
          type: "error",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else if (email && !isSubmitting) {
      handleFormSubmission();
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const canProceed = () => {
    if (currentStep === 1) return quizAnswers.teamSize !== "";
    if (currentStep === 2) return quizAnswers.englishCommunication !== "";
    if (currentStep === 3) return quizAnswers.challenges !== "";
    if (currentStep === 4) return email !== "";
    return false;
  };
  const progressPercentage = (currentStep / totalSteps) * 100;
  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.forBusinesses}
      <Header showLogout solidBackground={true} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary via-accent to-secondary">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
            Empower your tech team to communicate confidently in English
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 animate-fade-in max-w-3xl mx-auto">
            A scalable, AI-powered language solution designed for tech teams —
            no scheduling, no classrooms, just real communication progress.
          </p>
        </div>
      </section>

      {/* Dashboard Metrics Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            Team Performance Dashboard
          </h2>
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
            Get real-time insights into your team's English communication
            progress
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Daily Usage */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Daily Practice Sessions
                </CardTitle>
                <CardDescription>
                  Number of practice sessions this week
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dailyUsageData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis dataKey="day" stroke="hsl(var(--foreground))" />
                    <YAxis stroke="hsl(var(--foreground))" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="sessions"
                      fill="hsl(var(--primary))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Progress Over Time */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Average Score
                </CardTitle>
                <CardDescription>
                  Team performance improvement over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={progressData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis dataKey="week" stroke="hsl(var(--foreground))" />
                    <YAxis stroke="hsl(var(--foreground))" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={{
                        fill: "hsl(var(--primary))",
                        r: 6,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Most Practiced Conversation Types */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  Most Practiced Scenarios
                </CardTitle>
                <CardDescription>
                  Popular conversation types this month
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={conversationTypesData} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis type="number" stroke="hsl(var(--foreground))" />
                    <YAxis
                      dataKey="type"
                      type="category"
                      width={120}
                      stroke="hsl(var(--foreground))"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="hsl(var(--accent))"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Team Engagement */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-primary" />
                  Overall Team Engagement
                </CardTitle>
                <CardDescription>Active participation rate</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={engagementData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="hsl(var(--primary))"
                      dataKey="value"
                      label
                    >
                      {engagementData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <p className="text-3xl font-bold text-primary mt-4">
                  75% Active
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quiz Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="relative bg-gradient-to-br from-primary via-accent to-primary rounded-3xl p-12 sm:p-16 overflow-hidden shadow-2xl">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
              <div
                className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse"
                style={{
                  animationDelay: "1s",
                }}
              />
            </div>

            <div className="relative z-10">
              <div className="inline-block mb-6 mx-auto">
                <Sparkles className="w-16 h-16 text-white animate-pulse mx-auto" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
                Is GlobalCoders a fit for my tech team?
              </h2>
              <p className="text-lg text-white/95 mb-8 text-center font-medium">
                Take this quick 3-question quiz to find out
              </p>

              {!quizSubmitted ? (
                <Card className="shadow-xl bg-background">
                  <CardContent className="pt-6">
                    {/* Progress Bar */}
                    <div className="mb-8">
                      <div className="flex justify-between text-sm text-muted-foreground mb-2">
                        <span>
                          Step {currentStep} of {totalSteps}
                        </span>
                        <span>{Math.round(progressPercentage)}%</span>
                      </div>
                      <Progress value={progressPercentage} className="h-2" />
                    </div>

                    <div className="space-y-8">
                      {/* Question 1 */}
                      {currentStep === 1 && (
                        <div className="space-y-3 animate-fade-in">
                          <Label className="text-lg font-semibold">
                            How many employees are in your tech team?
                          </Label>
                          <Select
                            value={quizAnswers.teamSize}
                            onValueChange={(value) =>
                              setQuizAnswers({
                                ...quizAnswers,
                                teamSize: value,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select team size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1-10">
                                1-10 employees
                              </SelectItem>
                              <SelectItem value="11-50">
                                11-50 employees
                              </SelectItem>
                              <SelectItem value="51-100">
                                51-100 employees
                              </SelectItem>
                              <SelectItem value="100+">
                                100+ employees
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Question 2 */}
                      {currentStep === 2 && (
                        <div className="space-y-3 animate-fade-in">
                          <Label className="text-lg font-semibold">
                            Does your team currently communicate in English?
                          </Label>
                          <RadioGroup
                            value={quizAnswers.englishCommunication}
                            onValueChange={(value) =>
                              setQuizAnswers({
                                ...quizAnswers,
                                englishCommunication: value,
                              })
                            }
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id="english-yes" />
                              <Label
                                htmlFor="english-yes"
                                className="cursor-pointer"
                              >
                                Yes
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="sometimes"
                                id="english-sometimes"
                              />
                              <Label
                                htmlFor="english-sometimes"
                                className="cursor-pointer"
                              >
                                Sometimes
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="english-no" />
                              <Label
                                htmlFor="english-no"
                                className="cursor-pointer"
                              >
                                No
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}

                      {/* Question 3 */}
                      {currentStep === 3 && (
                        <div className="space-y-3 animate-fade-in">
                          <Label className="text-lg font-semibold">
                            Have you noticed communication challenges during
                            team meetings — like misunderstandings, or team
                            members not speaking up or asking questions?
                          </Label>
                          <RadioGroup
                            value={quizAnswers.challenges}
                            onValueChange={(value) =>
                              setQuizAnswers({
                                ...quizAnswers,
                                challenges: value,
                              })
                            }
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id="challenges-yes" />
                              <Label
                                htmlFor="challenges-yes"
                                className="cursor-pointer"
                              >
                                Yes
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="sometimes"
                                id="challenges-sometimes"
                              />
                              <Label
                                htmlFor="challenges-sometimes"
                                className="cursor-pointer"
                              >
                                Sometimes
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="challenges-no" />
                              <Label
                                htmlFor="challenges-no"
                                className="cursor-pointer"
                              >
                                No
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}

                      {/* Email Input */}
                      {currentStep === 4 && (
                        <div className="space-y-3 animate-fade-in">
                          <Label
                            htmlFor="email"
                            className="text-lg font-semibold"
                          >
                            Enter your work email to see results
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="your.email@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex justify-between gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleBack}
                          disabled={currentStep === 1}
                          className="flex items-center gap-2"
                        >
                          <ArrowLeft className="h-4 w-4" />
                          Back
                        </Button>
                        <Button
                          type="button"
                          onClick={handleNext}
                          disabled={!canProceed() || isSubmitting}
                          className="flex items-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              {currentStep === totalSteps
                                ? "See Results"
                                : "Next"}
                              {currentStep < totalSteps && (
                                <ArrowRight className="h-4 w-4" />
                              )}
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Error Message */}
                      {submitError && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                          <p className="text-sm text-destructive">
                            {submitError}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="relative">
                  {showConfetti && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                      {[...Array(50)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute animate-fade-in"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `-20px`,
                            animation: `fall ${2 + Math.random() * 3}s linear forwards`,
                            animationDelay: `${Math.random() * 0.5}s`,
                          }}
                        >
                          <div
                            className="w-3 h-3 rotate-45"
                            style={{
                              backgroundColor: [
                                "#8B5CF6",
                                "#EC4899",
                                "#F59E0B",
                                "#10B981",
                                "#3B82F6",
                              ][Math.floor(Math.random() * 5)],
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <Card className="shadow-xl bg-background border-primary relative z-10">
                    <CardContent className="pt-6 text-center">
                      <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4 animate-scale-in" />
                      <h3 className="text-2xl font-bold text-foreground mb-2">
                        Great news! GlobalCoders is a great fit for your team!
                        🎉
                      </h3>
                      <p className="text-foreground/80 mb-4">
                        Great news! GlobalCoders is a good fit for your team. 🎉
                      </p>
                      <p className="text-foreground/80">
                        We will get in touch soon.
                      </p>
                    </CardContent>
                  </Card>
                  <style>{`
                @keyframes fall {
                  to {
                    transform: translateY(100vh) rotate(360deg);
                    opacity: 0;
                  }
                }
              `}</style>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            How GlobalCoders Helps Your Team
          </h2>
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
            AI-powered simulations designed for real workplace scenarios
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <MessageSquare className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Boost Team Confidence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Practice real work scenarios in a safe environment before
                  critical meetings
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Reduce Misunderstandings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Clear communication leads to fewer errors and smoother
                  collaboration
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Brain className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Practice Real Scenarios</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  From daily stand-ups to client presentations, practice what
                  matters most
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <Sparkles className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Track Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Measure improvement with detailed analytics and personalized
                  feedback
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Professional Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Global Coders
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                AI-powered English conversation practice for tech professionals.
                Build confidence, improve communication, and ace your next
                conversation.
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/subscription-billing"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Subscription and Billing Terms
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Get in Touch
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Have questions or feedback? We'd love to hear from you. Contact
                us on support@globalcoders.app - we do respond to all emails!
              </p>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={footerFunding}
                  alt="Financiado por la Unión Europea - NextGenerationEU. Con el apoyo de la Generalitat de Catalunya"
                  className="max-w-full h-auto"
                  style={{
                    maxHeight: "140px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Global Coders. All rights
                reserved.
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span>Made with</span>
                <span className="text-white/80">❤️</span>
                <span>for developers worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
